import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { NEUTRAL_500, WHITE, NEUTRAL_400 } from 'visitor-ui-component-library/constants/WidgetColors';
import { COMPOSER_INLINE_MARGIN, COMPOSER_BOTTOM_MARGIN } from '../widget-dimensions/constants/dimensions';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SLIDE_UP_ANIMATION_DURATION = 0.5;
const FADE_IN_ANIMATION_DURATION = 1;
export const ANIMATION_DELAY = 1000;
const ComposerWrapper = styled.div.withConfig({
  displayName: "MessageComposer__ComposerWrapper",
  componentId: "sc-1n6qkwa-0"
})(["position:relative;overflow:hidden;min-height:", "px;"], ({
  $composerHeight
}) => $composerHeight + COMPOSER_BOTTOM_MARGIN);
const Composer = styled.div.withConfig({
  displayName: "MessageComposer__Composer",
  componentId: "sc-1n6qkwa-1"
})(["max-width:100%;background-color:", ";flex-shrink:0;transition:margin ", "s linear;margin:0 ", "px ", "px;border:1px solid ", ";border-radius:28px;padding:0 8px;&:focus-within{border-color:", ";}[data-test-id='widget-textarea']{min-height:38px !important;}"], WHITE, SLIDE_UP_ANIMATION_DURATION, COMPOSER_INLINE_MARGIN, ({
  $showAIDisclaimer,
  $aIDisclaimerHeight
}) => $showAIDisclaimer && $aIDisclaimerHeight > 0 ? $aIDisclaimerHeight + 5 : COMPOSER_BOTTOM_MARGIN, ({
  $disabled
}) => $disabled ? NEUTRAL_400 : NEUTRAL_500, ({
  $accentColor
}) => $accentColor);
const Inner = styled.div.withConfig({
  displayName: "MessageComposer__Inner",
  componentId: "sc-1n6qkwa-2"
})(["display:flex;width:100%;align-items:center;justify-content:space-between;flex-wrap:nowrap;gap:4px;"]);
const InputWrapper = styled.div.withConfig({
  displayName: "MessageComposer__InputWrapper",
  componentId: "sc-1n6qkwa-3"
})(["flex:1 1 0px;"]);
const ActionsWrapper = styled.div.withConfig({
  displayName: "MessageComposer__ActionsWrapper",
  componentId: "sc-1n6qkwa-4"
})(["display:flex;flex:0 0 auto;gap:4px;", ""], ({
  children
}) => !children && 'display: none;');
const AIDisclaimer = styled.div.withConfig({
  displayName: "MessageComposer__AIDisclaimer",
  componentId: "sc-1n6qkwa-5"
})(["font-size:0.9em;text-align:center;opacity:", ";width:100%;transition:opacity ", "s ease-in;position:absolute;padding:0 20px;bottom:3px;"], ({
  $isVisible
}) => $isVisible ? '1' : '0', FADE_IN_ANIMATION_DURATION);
export default function MessageComposer({
  FileUploads = null,
  inputToRender,
  children,
  disabled,
  enableAIDisclaimer = false,
  accentColor = NEUTRAL_500
}) {
  const [showAIDisclaimer, setShowAIDisclaimer] = useState(false);
  const [aIDisclaimerHeight, setAIDisclaimerHeight] = useState(0);
  const disclaimerRef = useRef(null);
  const [composerHeight, setComposerHeight] = useState(0);
  const composerRef = useRef(null);
  useEffect(() => {
    if (enableAIDisclaimer) {
      const timer = setTimeout(() => {
        setShowAIDisclaimer(true);
      }, ANIMATION_DELAY);
      return () => clearTimeout(timer);
    }
  }, [enableAIDisclaimer]);
  useLayoutEffect(() => {
    if (showAIDisclaimer && disclaimerRef.current) {
      const height = disclaimerRef.current.offsetHeight;
      setAIDisclaimerHeight(height);
    }
  }, [showAIDisclaimer]);
  useLayoutEffect(() => {
    if (composerRef.current) {
      setComposerHeight(composerRef.current.offsetHeight);
    }
  }, [composerRef]);
  return /*#__PURE__*/_jsxs(ComposerWrapper, {
    showAIDisclaimer: showAIDisclaimer,
    $aIDisclaimerHeight: aIDisclaimerHeight,
    $composerHeight: composerHeight,
    "data-test-id": "composer-wrapper",
    children: [/*#__PURE__*/_jsxs(Composer, {
      $disabled: disabled,
      $accentColor: accentColor,
      $showAIDisclaimer: showAIDisclaimer,
      $aIDisclaimerHeight: aIDisclaimerHeight,
      ref: composerRef,
      children: [FileUploads, /*#__PURE__*/_jsxs(Inner, {
        children: [/*#__PURE__*/_jsx(InputWrapper, {
          children: inputToRender
        }), /*#__PURE__*/_jsx(ActionsWrapper, {
          children: children
        })]
      })]
    }), enableAIDisclaimer && /*#__PURE__*/_jsx(AIDisclaimer, Object.assign({
      ref: disclaimerRef,
      $isVisible: showAIDisclaimer
    }, showAIDisclaimer && {
      'aria-label': I18n.text('conversations-visitor-experience-components.aiDisclaimer')
    }, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "conversations-visitor-experience-components.aiDisclaimer"
      })
    }))]
  });
}
MessageComposer.displayName = 'MessageComposer';